import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { AppView, Button, CardView, SettingsItemSwitch, Spacer } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useEventBusEffect, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { editSavingsPlan, updateSavingRules } from 'features/pots/actions';
import AutosaveRulesWarningModal from 'features/pots/components/AutosaveRulesWarningModal';
import { busEvents } from 'features/pots/constants';
import { selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import { roundUpOptions, SavingsPlanRuleType } from 'features/pots/types';
import createStyleSheets from 'utils/createStyleSheets';
const AutosaveRoundupSettings = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const [, , , fetch] = useFetchOld(muteAll);
    const mountRef = useRef(false);
    const store = useAppStore();
    const autosaveRulesArr = useAppSelector(selectAutosaveRulesArr);
    const savingsPlanId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const isPaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const roundUpMultiplier = useAppSelector((state) => state.pots.savingsPlan?.roundUpMultiplier);
    const [selectedRound, setSelectedRound] = useState(isPaused ? undefined : roundUpMultiplier);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_RULES}/>,
        });
    }, []);
    useEffect(() => {
        if (mountRef.current) {
            setSelectedRound(roundUpMultiplier);
        }
        else {
            mountRef.current = true;
        }
    }, [roundUpMultiplier]);
    const pauseAutosave = useCallback(() => {
        if (!savingsPlanId || isPaused)
            return;
        fetch(editSavingsPlan(savingsPlanId, { isPaused: true }));
    }, [isPaused, savingsPlanId]);
    useEventBusEffect(() => {
        setSelectedRound(store.getState().pots.savingsPlan?.isPaused ? undefined : store.getState().pots.savingsPlan?.roundUpMultiplier);
    }, [busEvents.UPDATE_SAVING_RULES_FAILURE_EVENT]);
    const onSwitchEvent = useMemo(() => debounce(async (value) => {
        if (savingsPlanId) {
            const newRules = (() => {
                if (isPaused) {
                    return [{ type: SavingsPlanRuleType.ROUND_UP, roundUpMultiplier: 1 }];
                }
                return value
                    ? [...autosaveRulesArr, { type: SavingsPlanRuleType.ROUND_UP, roundUpMultiplier: 1 }]
                    : autosaveRulesArr.filter((rule) => rule.type !== SavingsPlanRuleType.ROUND_UP);
            })();
            const oldSelectedRound = selectedRound;
            setSelectedRound(value ? 1 : undefined);
            if (!newRules.length) {
                AutosaveRulesWarningModal.show(pauseAutosave, () => {
                    setSelectedRound(oldSelectedRound);
                });
            }
            else {
                dispatch(updateSavingRules({
                    savingsPlanId,
                    rules: newRules,
                }));
            }
        }
    }, 200), [autosaveRulesArr, isPaused, pauseAutosave, savingsPlanId, selectedRound]);
    const onChangeRound = useCallback((round) => async () => {
        if (savingsPlanId && round !== selectedRound && roundUpOptions.includes(round)) {
            setSelectedRound(round);
            const newRules = autosaveRulesArr.filter((rule) => rule.type !== SavingsPlanRuleType.ROUND_UP);
            newRules.push({
                roundUpMultiplier: round,
                type: SavingsPlanRuleType.ROUND_UP,
            });
            dispatch(updateSavingRules({
                savingsPlanId,
                rules: newRules,
            }));
        }
    }, [autosaveRulesArr, savingsPlanId, selectedRound]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Round Ups" subTitle="Emma will round up your transactions from all active linked accounts to the nearest £1 and transfer the spare change to your savings pots."/>
          <CardView noMarginTop>
            <SettingsItemSwitch title="Round Ups" value={!!selectedRound} onValueChange={onSwitchEvent}/>
            {!!selectedRound && (<View testID="roundUpMain">
                <View style={styles.main}>
                  <Text Text-16>Super round ups</Text>
                  <Text TextThin-14 Secondary>
                    Use the multiplier to save faster:
                  </Text>
                  <Spacer.H16 />
                </View>
                <View style={styles.buttonContainer}>
                  {roundUpOptions.map((option) => (<Button flex square key={String(option)} title={`${option}x`} style={styles.roundBtn} onPress={onChangeRound(option)} {...(selectedRound === option ? { brandLight: true } : { secondary: true, gray: true })}/>))}
                </View>
              </View>)}
          </CardView>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    main: {
        paddingTop: rem(16),
        borderTopWidth: rem(1),
        paddingHorizontal: rem(16),
        borderTopColor: colors.background.dark,
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingBottom: rem(16),
        paddingHorizontal: rem(12),
    },
    roundBtn: {
        marginHorizontal: rem(4),
    },
}));
export default AutosaveRoundupSettings;
